import * as THREE from 'three';

export interface PixelationConfig {
  width: number;
  height: number;
  enabled: boolean;
}

const VERT = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

const FRAG = `
  uniform sampler2D uTex;
  varying vec2 vUv;
  void main() {
    gl_FragColor = texture2D(uTex, vUv);
  }
`;

/**
 * Two-pass pixelation: scene -> low-res WebGLRenderTarget (NEAREST),
 * then a fullscreen NEAREST blit back to the canvas at native resolution.
 * Keeps the racer model "3D-pixel" rather than smooth-3D-on-pixels.
 */
export class PixelationPipeline {
  private target: THREE.WebGLRenderTarget;
  private quadScene: THREE.Scene;
  private quadCamera: THREE.OrthographicCamera;
  private quadMesh: THREE.Mesh;
  private uniforms: { uTex: { value: THREE.Texture | null } };

  constructor(public config: PixelationConfig) {
    this.target = new THREE.WebGLRenderTarget(config.width, config.height, {
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      wrapS: THREE.ClampToEdgeWrapping,
      wrapT: THREE.ClampToEdgeWrapping,
      depthBuffer: true,
      stencilBuffer: false,
      colorSpace: THREE.SRGBColorSpace,
    });

    this.quadScene = new THREE.Scene();
    this.quadCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this.uniforms = { uTex: { value: this.target.texture } };
    const mat = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: this.uniforms,
      depthTest: false,
      depthWrite: false,
    });
    this.quadMesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), mat);
    this.quadScene.add(this.quadMesh);
  }

  setResolution(width: number, height: number): void {
    width = Math.max(64, Math.floor(width));
    height = Math.max(36, Math.floor(height));
    if (width === this.config.width && height === this.config.height) return;
    this.config.width = width;
    this.config.height = height;
    this.target.setSize(width, height);
  }

  /** Render scene through the pipeline (or directly if disabled). */
  render(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    camera: THREE.Camera,
  ): void {
    if (!this.config.enabled) {
      renderer.setRenderTarget(null);
      renderer.render(scene, camera);
      return;
    }
    if (camera instanceof THREE.PerspectiveCamera) {
      // The aspect of the off-screen target should follow the actual canvas aspect
      // so pixel cells stay square on screen.
    }
    renderer.setRenderTarget(this.target);
    renderer.clear(true, true, false);
    renderer.render(scene, camera);

    renderer.setRenderTarget(null);
    this.uniforms.uTex.value = this.target.texture;
    renderer.render(this.quadScene, this.quadCamera);
  }

  dispose(): void {
    this.target.dispose();
    (this.quadMesh.material as THREE.Material).dispose();
    this.quadMesh.geometry.dispose();
  }
}
